import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DetailItem, DetailsList } from '../../components/DetailsList';
import { Drawer } from '../../components/Drawer';
import { HelpDrawerLink } from '../../components/HelpDrawerLink';
import { formatDate } from '../../utils';
import { useContractData } from './context';
import { getContractStatusLabel } from './utils/contractStatus';
import { ContractFormActions } from './components';
import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';

function ContractDetailsTab() {
  const { contractId, contract } = useContractData();
  const navigate = useNavigate();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const baseTestId = 'contract-details-tab';
  const { hasRoleOnContract, isAdmin } = useRBAC();
  const canViewUpdateRoster = isAdmin || hasRoleOnContract(contractId ?? '', CONTRACT_ROLE.ROSTER_OWNER);

  if (!contract) {
    return (
      <div
        className='contract-details-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-empty`}>
          No contract information available.
        </p>
      </div>
    );
  }

  const primaryCorName = contract.primaryCor ? `${contract.primaryCor.firstName} ${contract.primaryCor.lastName}` : '';

  const contractDetails: DetailItem[] = [
    { label: 'Contract Number', value: contract.contractNumber || '' },
    { label: 'Company', value: contract.companyName || '' },
    { label: 'Contract Name', value: contract.contractName || '' },
    { label: 'Effective Date', value: formatDate(contract.contractStartDate) },
    { label: 'Ultimate Completion Date', value: formatDate(contract.contractEndDate) },
    { label: 'Federal Approver', value: primaryCorName || '' },
    { label: 'Contracting Officer', value: '' },
    { label: 'Contract Status', value: getContractStatusLabel(contract.contractStatus) },
    {
      label: 'Last Roster Review Date',
      links: [
        ...(canViewUpdateRoster
          ? [
              {
                label: 'Certify Roster',
                onClick: (e) => {
                  e.preventDefault();
                  navigate(`/contract/${contractId}/roster`);
                },
              },
            ]
          : []),
      ],
      value: formatDate(contract.lastRosterReviewDate),
    },
    {
      label: 'Next Roster Update Due Date',
      value: formatDate(contract.nextRosterUpdateDueDate),
    },
  ];

  return (
    <div
      className='contract-details-tab-content'
      data-testid={baseTestId}>
      <div
        className='contract-settings-content-container'
        data-testid={`${baseTestId}-content`}>
        <p
          className='ds-u-margin-bottom--0'
          data-testid={`${baseTestId}-intro-text`}>
          If any of the information in this section is incorrect, please work with your Contracting Officer to have it
          updated.
        </p>

        <DetailsList
          data-testid={`${baseTestId}-details`}
          header='Contract Details'
          items={contractDetails}
        />

        <HelpDrawerLink
          className='ds-u-margin-top--2'
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpOpen(true)}
        />

        <Drawer
          data-testid={`${baseTestId}-drawer`}
          heading='Help: Contract Details'
          isOpen={isHelpOpen}
          width='50%'
          onClose={() => setIsHelpOpen(false)}>
          <h3>Contract Number</h3>
          <p>The Contract Number is a unique number given to a Contract when it is first created.</p>
          {/*Note: Contract Name will be added post-MVP.*/}
          {/*<h3>Contract Name</h3>
          <p>
            The Contract Name is a nickname or label added to a Contract when it is created. This is used to find a
            specific Contract.
          </p>*/}
          <h3>Company</h3>
          <p>The name of the contracting company that is assigned to the Contract.</p>
          <h3>Effective Date</h3>
          <p>This is the date the Contract is set to begin.</p>
          <h3>Ultimate Completion Date</h3>
          <p>
            This is the date the Contract is set to end when it is awarded. It may change if the Contract is extended.
          </p>
          <h3>Federal Approver</h3>
          <p>
            This is the name of the Federal Approver (COR) for this contract. This will help you find the Federal
            Approver (COR) in AccessCMS.
          </p>

          {/* NOTE: These items are still in progress, restore it when HCD marks the final content as complete */}
          {/*<h3>Contractor Officer ID</h3>
          <p>
            This is the EUA ID for the Contractor Officer of this Contract. This is helpful for searching for the
            Contractor Officer within AccessCMS.
          </p>*/}
          {/*<h3>Contract Status</h3>
          <p>This is the status of the Contract: active, on hold, or extended once. </p>*/}

          <h3>Last Roster Date</h3>
          <p>
            This is the date the Roster was last certified. The Roster Owner for the Contract must confirm that the
            Roster of Users is correct every 30 days, as users may be added or removed from the Roster at any time. A
            warning icon may appear here when the next due date is soon or in the past.
          </p>
          <h3>Next Roster Update Due Date</h3>
          <p>
            The Roster must be recertified before this date. The Roster Owner for the Contract must confirm that the
            Roster of Users is correct every 30 days, as users may be added or removed from the Roster at any time.
          </p>
        </Drawer>

        <ContractFormActions
          data-testid={`${baseTestId}-actions`}
          isEditable={false}
        />
      </div>
    </div>
  );
}

export default ContractDetailsTab;
